import { createContext, Dispatch, FC, ReactNode, SetStateAction, useCallback, useContext, useState } from 'react';

export const INDICATOR_LOADING_STATE = 'loading';

type IndicatorData = {
  graphData: boolean | typeof INDICATOR_LOADING_STATE;
  statsData: boolean | typeof INDICATOR_LOADING_STATE;
};

type IndicatorContextInterface = {
  hasData: IndicatorData;
  setHasData: Dispatch<SetStateAction<IndicatorData>>;
};

const IndicatorContext = createContext<IndicatorContextInterface | null>(null);

export const IndicatorProvider: FC<{ children: ReactNode; defaultState?: IndicatorData }> = ({
  children,
  defaultState,
}) => {
  const [hasData, setHasData] = useState<IndicatorData>(
    defaultState ?? {
      graphData: INDICATOR_LOADING_STATE,
      statsData: INDICATOR_LOADING_STATE,
    },
  );

  return <IndicatorContext.Provider value={{ hasData, setHasData }}>{children}</IndicatorContext.Provider>;
};

const useIndicatorContext = (): IndicatorContextInterface => {
  const indicatorContextData = useContext(IndicatorContext);

  if (!indicatorContextData) {
    throw new Error(
      `The component that calls useIndicatorContext() needs to be a deep child of <IndicatorContextProvider>`,
    );
  }

  return indicatorContextData;
};

export const useSetIndicatorHasData = () => {
  const { setHasData } = useIndicatorContext();

  const setIndicatorGraphData = useCallback(
    ({ graphData }: Pick<IndicatorData, 'graphData'>) => {
      setHasData((prev: IndicatorData) => ({
        graphData,
        statsData: prev.statsData,
      }));
    },
    [setHasData],
  );

  const setIndicatorStatsData = useCallback(
    ({ statsData }: Pick<IndicatorData, 'statsData'>) => {
      setHasData((prev: IndicatorData) => ({
        graphData: prev.graphData,
        statsData,
      }));
    },
    [setHasData],
  );

  return { setIndicatorGraphData, setIndicatorStatsData };
};

const useIsDataLoading = (): boolean => {
  const { hasData } = useIndicatorContext();

  return hasData.graphData === INDICATOR_LOADING_STATE || hasData.statsData === INDICATOR_LOADING_STATE;
};

export const useHasIndicatorData = (): boolean => {
  const { hasData } = useIndicatorContext();

  const isDataLoading = useIsDataLoading();

  if (isDataLoading) {
    return true; // Allow loading data
  }

  return !!(hasData.graphData || hasData.statsData);
};
